import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiLogOut,
  FiCheckCircle, FiAlertCircle, FiClock, FiDollarSign,
  FiCalendar, FiUser, FiMapPin, FiHome, FiHeadphones
} from 'react-icons/fi';
import { usePortal } from '../../context/PortalContext';
import { SmokeBackground } from '../../components/ui/spooky-smoke-animation';

// Mapa visual por serviço
const SERVICO_META = {
  sintia:  { label: 'PapIA',  color: 'from-green-500 to-blue-600',    bg: 'bg-gradient-to-br from-green-50 to-blue-50',    border: 'border-green-200',  dot: 'bg-green-500', desc: 'Inteligência Artificial para automação de atendimento e suporte via WhatsApp.'  },
  lucrum:  { label: 'Lucrum',  color: 'from-yellow-500 to-orange-500', bg: 'bg-gradient-to-br from-yellow-50 to-orange-50', border: 'border-yellow-200', dot: 'bg-yellow-500', desc: 'Sistema de gestão financeira e controle de vendas para o seu negócio.' },
  delivex: { label: 'Delivex', color: 'from-orange-500 to-red-500',    bg: 'bg-gradient-to-br from-orange-50 to-red-50',    border: 'border-orange-200', dot: 'bg-orange-500', desc: 'Plataforma de delivery completa com cardápio digital e gestão de pedidos.' },
  rotas:   { label: 'Rotas',   color: 'from-purple-500 to-indigo-600', bg: 'bg-gradient-to-br from-purple-50 to-indigo-50', border: 'border-purple-200', dot: 'bg-purple-500', desc: 'Otimização e rastreamento de rotas para entregadores em tempo real.'  },
};

const STATUS_META = {
  pago:     { label: 'Pago',     icon: FiCheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-50' },
  pendente: { label: 'A vencer', icon: FiClock,       color: 'text-amber-700',   bg: 'bg-amber-50'   },
  atrasado: { label: 'Atrasado', icon: FiAlertCircle, color: 'text-red-700',     bg: 'bg-red-50'     },
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (dateVal) => {
  if (!dateVal) return '—';
  try {
    const str = String(dateVal);
    // Match "YYYY-MM-DD" no início (cobre "YYYY-MM-DD" puro e ISO datetime)
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    // Fallback: tenta parsear como Date
    const d = dateVal instanceof Date ? dateVal : new Date(str);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR');
  } catch {
    return '—';
  }
};

// Mascara endereço: mostra primeiras palavras e mascara o resto
const maskAddress = (endereco, cidade, estado) => {
  if (!endereco && !cidade) return '—';
  let full = endereco || '';
  if (cidade) full += (full ? ' — ' : '') + cidade;
  if (estado) full += '/' + estado;
  // Mascara: mostra até ~40% e mascara o resto
  const words = full.split(' ');
  const showCount = Math.max(2, Math.ceil(words.length * 0.4));
  const visible = words.slice(0, showCount).join(' ');
  const masked = words.slice(showCount).map(w => w.replace(/[A-Za-zÀ-ÿ0-9]/g, '*')).join(' ');
  return visible + (masked ? ' ' + masked : '');
};

// ──────────────────────────────────────────
//  Sidebar
// ──────────────────────────────────────────
const Sidebar = () => (
  <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col justify-between border-r border-white/10 bg-black/25 px-6 py-8 shadow-[8px_0_32px_rgba(0,0,0,0.22),inset_-1px_0_0_rgba(255,255,255,0.08)] backdrop-blur-xl lg:flex">
    <div>
      <div className="select-none border-b border-white/10 pb-7">
        <h1 className="font-display text-3xl font-bold tracking-tighter text-white">AyTech</h1>
        <p className="mt-1 text-xs uppercase tracking-[0.22em] text-white/45">Portal do cliente</p>
      </div>

      <div className="mt-8 flex items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.08] px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-black/10 backdrop-blur-xl">
        <FiHome className="h-4 w-4" />
        Visão geral
      </div>
    </div>

    <a
      href="https://wa.me/5541987718296"
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.08] px-4 py-3 text-sm font-semibold text-white/80 shadow-lg shadow-black/10 backdrop-blur-xl transition hover:border-white/30 hover:bg-white/[0.14] hover:text-white"
    >
      <FiHeadphones className="h-5 w-5" />
      Falar com o suporte
    </a>
  </aside>
);

// ──────────────────────────────────────────
//  Card de Fatura
// ──────────────────────────────────────────
const FaturaCard = ({ total, vencimento, status, planos, onPaymentStarted, onVerificar }) => {
  const { criarPagamentoPix } = usePortal();
  const statusKey = status || 'pendente';
  const s = STATUS_META[statusKey] || STATUS_META.pendente;
  const StatusIcon = s.icon;
  const isPago = statusKey === 'pago';
  const [pixLoading, setPixLoading] = useState(null);
  const [pagamentoAberto, setPagamentoAberto] = useState(false);
  const [verificandoManual, setVerificandoManual] = useState(false);
  const planosNaoPagos = (planos || []).filter(p => p.ativo && p.status_pagamento !== 'pago');

  const handlePagarPix = async (plano) => {
    setPixLoading(plano.id);
    const result = await criarPagamentoPix(plano.id);
    setPixLoading(null);
    if (result.success && result.pagamento?.init_point) {
      window.open(result.pagamento.init_point, '_blank');
      setPagamentoAberto(true);
      if (onPaymentStarted) onPaymentStarted(result.pagamento.id);
    } else {
      alert(result.message || 'Erro ao gerar pagamento.');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full overflow-hidden rounded-3xl border border-black/10 bg-white/75 shadow-xl shadow-black/5 backdrop-blur-xl"
    >
      <div className="p-6 sm:p-7">
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.color} mb-4`}>
          <StatusIcon className="w-3.5 h-3.5" />
          {s.label}
        </div>

        <div className="flex items-baseline gap-1 mb-1">
          <span className="text-4xl font-bold tracking-tight text-gray-950">{formatCurrency(total)}</span>
        </div>

        <div className="mb-6 flex items-center gap-1.5 text-sm text-gray-500">
          <FiCalendar className="w-4 h-4" />
          <span>Vence dia <strong className="font-semibold text-gray-800">{formatDate(vencimento)}</strong></span>
        </div>

        {/* Botões PIX por plano */}
        {!isPago && planosNaoPagos.length > 0 && (
          <div className="space-y-2">
            {planosNaoPagos.map((plano) => {
              const meta = SERVICO_META[plano.servico] || SERVICO_META.sintia;
              return (
                <button
                  key={plano.id}
                  onClick={() => handlePagarPix(plano)}
                  disabled={pixLoading === plano.id}
                  className="relative isolate flex w-full items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a] py-3.5 text-sm font-bold text-white shadow-lg shadow-black/15 transition duration-300 hover:brightness-125 active:scale-[0.99] disabled:cursor-wait disabled:opacity-60"
                >
                  <span className="pointer-events-none absolute inset-0 z-0">
                    <SmokeBackground smokeColor="#2f2f2f" />
                    <span className="absolute inset-0 bg-black/25" />
                  </span>
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {pixLoading === plano.id ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Gerando pagamento...
                      </>
                    ) : (
                      <>
                        <FiDollarSign className="h-4 w-4" />
                        Pagar {meta.label} — {formatCurrency(plano.valor_mensal)}
                      </>
                    )}
                  </span>
                </button>
              );
            })}

            {/* Aviso: aparece após abrir o checkout do MP */}
            {pagamentoAberto && (
              <div className="mt-3 space-y-2">
                <div className="flex items-start gap-2.5 rounded-2xl border border-black/10 bg-black/[0.03] px-4 py-3 text-sm leading-relaxed text-gray-600">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
                  </svg>
                  <span>
                    Após <strong>efetuar o pagamento</strong> no Mercado Pago, você pode{' '}
                    <strong>fechar a aba</strong> e retornar aqui.{' '}
                    O status será atualizado automaticamente em até <strong>1 minuto</strong>.
                  </span>
                </div>

                {/* Botão de verificação manual */}
                <button
                  onClick={async () => {
                    if (!onVerificar || verificandoManual) return;
                    setVerificandoManual(true);
                    await onVerificar();
                    setVerificandoManual(false);
                  }}
                  disabled={verificandoManual}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-black/15 bg-white/60 py-2.5 text-sm font-semibold text-gray-800 transition hover:bg-white disabled:opacity-60"
                >
                  {verificandoManual ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Verificando...
                    </>
                  ) : (
                    <>
                      <FiCheckCircle className="w-4 h-4" />
                      Já paguei — Verificar agora
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {isPago && (
          <div className="flex w-full items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 py-3 text-sm font-bold text-emerald-700">
            <FiCheckCircle className="w-4 h-4" />
            Tudo pago!
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ──────────────────────────────────────────
//  Seção "Seu Plano" na página Início
// ──────────────────────────────────────────
const SeuPlanoSection = ({ planos, user }) => {
  const planosAtivos = planos.filter(p => p.ativo);
  if (planosAtivos.length === 0) return null;

  const maskedAddr = maskAddress(user.endereco, user.cidade, user.estado);

  return (
    <div>
      <h3 className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
        <span className="inline-block h-4 w-1 rounded-full bg-gray-400" />
        Seu plano
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {planosAtivos.map((plano, i) => {
          const meta = SERVICO_META[plano.servico] || SERVICO_META.sintia;
          const statusKey = plano.status_pagamento || 'pendente';
          const s = STATUS_META[statusKey] || STATUS_META.pendente;

          return (
            <motion.div
              key={plano.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="rounded-3xl border border-black/10 bg-white/75 p-6 shadow-xl shadow-black/5 backdrop-blur-xl"
            >
              {/* Badge status */}
              <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold ${s.bg} ${s.color} mb-3`}>
                {s.label}
              </div>

              {/* Nome do plano */}
              <h4 className="mb-1 text-lg font-bold text-gray-950">
                {plano.plano_nome || `${meta.label} PLANO`}
              </h4>

              {/* Descrição */}
              <p className="mb-4 text-sm leading-relaxed text-gray-500">
                {meta.desc}
              </p>

              {/* Valor */}
              <div className="flex items-baseline gap-1 mb-3">
                <span className="text-2xl font-bold text-gray-950">{formatCurrency(plano.valor_mensal)}</span>
                <span className="text-sm text-gray-400">/mês</span>
              </div>

              {/* Endereço mascarado */}
              <div className="mb-2">
                <p className="flex items-center gap-1 text-xs font-bold text-gray-700">
                  <FiMapPin className="w-3 h-3" /> Endereço
                </p>
                <p className="font-mono text-xs text-gray-400">{maskedAddr}</p>
              </div>

              {/* Contrato */}
              <div>
                <p className="text-xs font-bold text-gray-700">Contrato</p>
                <p className="font-mono text-sm font-semibold text-gray-950">{plano.contrato || '—'}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────
//  Dashboard principal
// ──────────────────────────────────────────
const PortalDashboard = () => {
  const { user, planos, logout, totalFatura, proximoVencimento, isLoggedIn, refreshDashboard, verificarStatusPix } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();
  const pollRef = useRef(null);
  const [pollingAtivo, setPollingAtivo] = useState(false);

  // Proteção de rota
  React.useEffect(() => {
    if (!isLoggedIn) navigate('/portal', { replace: true });
  }, [isLoggedIn, navigate]);

  // Detecta redirect do Mercado Pago com status na URL
  useEffect(() => {
    const params    = new URLSearchParams(location.search);
    const mpStatus  = params.get('collection_status') || params.get('status');
    const paymentId = params.get('collection_id')      || params.get('payment_id');

    if (mpStatus === 'approved' && paymentId) {
      // Limpa os params da URL sem recarregar
      navigate(location.pathname, { replace: true });

      // 1) Confirma o pagamento no backend usando o ID do MP (corrige o banco imediatamente)
      fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/portal/pix/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('portal_token')}`,
        },
        body: JSON.stringify({ mp_payment_id: paymentId }),
      })
        .then(() => {
          // 2) Depois de confirmar, atualiza o dashboard
          refreshDashboard();
        })
        .catch(() => {
          // Fallback: tenta atualizar mesmo sem confirmação
          refreshDashboard();
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Para o polling quando o plano é marcado como pago
  useEffect(() => {
    const algumPago = planos.some(p => p.status_pagamento === 'pago');
    if (algumPago && pollingAtivo) {
      clearInterval(pollRef.current);
      setPollingAtivo(false);
    }
  }, [planos, pollingAtivo]);

  // Limpa o intervalo ao desmontar
  useEffect(() => {
    return () => clearInterval(pollRef.current);
  }, []);

  // Chamado quando o usuário abre o checkout do MP (polling ativo que verifica o MP diretamente)
  const handlePaymentStarted = useCallback((pagamentoId) => {
    if (pollingAtivo) return;
    setPollingAtivo(true);
    let tentativas = 0;
    const MAX = 100; // 100 × 6s = 10 min
    pollRef.current = setInterval(async () => {
      tentativas++;

      // Chama /api/portal/pix/status que busca no MP por external_reference
      if (pagamentoId) {
        try {
          const res = await verificarStatusPix(pagamentoId);
          if (res?.pagamento?.status === 'approved') {
            // Pagamento confirmado — atualiza a UI e para o polling
            refreshDashboard();
            clearInterval(pollRef.current);
            setPollingAtivo(false);
            return;
          }
        } catch { /* continua tentando */ }
      }

      // Fallback: atualiza o dashboard de qualquer forma
      refreshDashboard();

      if (tentativas >= MAX) {
        clearInterval(pollRef.current);
        setPollingAtivo(false);
      }
    }, 6000);
  }, [pollingAtivo, refreshDashboard, verificarStatusPix]);

  const handleLogout = () => {
    clearInterval(pollRef.current);
    logout();
    navigate('/portal');
  };

  if (!user) return null;

  const statusGeral = planos.some(p => p.status_pagamento === 'atrasado')
    ? 'atrasado'
    : planos.some(p => p.status_pagamento === 'pendente')
      ? 'pendente'
      : 'pago';

  // Header: contrato do assinante + endereço mascarado
  const firstContrato = user.contrato || (planos.length > 0 ? planos[0].contrato : null) || user.id?.toString().padStart(6, '0');
  const headerAddr = maskAddress(user.endereco, user.cidade, user.estado);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f4f4f3] text-gray-950">
      <div className="pointer-events-none fixed inset-0 z-0 bg-[#0a0a0a]">
        <SmokeBackground smokeColor="#2f2f2f" />
      </div>

      <div className="relative z-10 flex min-h-screen">
        {/* Sidebar (desktop only) */}
        <Sidebar />

        {/* Conteúdo principal */}
        <div className="flex min-w-0 flex-1 flex-col">
        {/* Header top */}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-white/10 bg-black/25 px-4 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.20),inset_0_-1px_0_rgba(255,255,255,0.08)] backdrop-blur-xl lg:px-8">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/85">
                Contrato {firstContrato} <span className="font-normal normal-case tracking-normal text-white/45">· {headerAddr}</span>
              </p>
            </div>
          </div>

          <button
            id="btn-sair-header"
            onClick={handleLogout}
            className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.08] px-3 py-2 text-sm text-white/70 backdrop-blur-xl transition hover:border-white/30 hover:bg-white/[0.14] hover:text-white"
          >
            <FiLogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sair</span>
          </button>
        </header>

        {/* Conteúdo */}
        <main className="flex-1 bg-[#f4f4f3] p-4 sm:p-6 lg:p-8">
          {/* Única seção: Início */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-auto max-w-6xl space-y-10"
          >
            {/* Saudação */}
            <div>
              <p className="text-sm text-gray-500">Seja bem-vindo(a),</p>
              <h2 className="font-display text-3xl font-bold tracking-tight text-gray-950 sm:text-4xl">{user.nome}</h2>
              {user.empresa && (
                <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
                  <FiUser className="w-4 h-4" />
                  <span>{user.empresa}</span>
                </div>
              )}
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
              {/* Fatura */}
              <div>
                <h3 className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  <span className="inline-block h-4 w-1 rounded-full bg-gray-400" />
                  Priorizar pagamento
                </h3>
                <FaturaCard
                  total={totalFatura}
                  vencimento={proximoVencimento}
                  status={statusGeral}
                  planos={planos}
                  onPaymentStarted={handlePaymentStarted}
                  onVerificar={refreshDashboard}
                />
              </div>

              {/* Acesso rápido */}
              <div>
                <h3 className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  <span className="inline-block h-4 w-1 rounded-full bg-gray-400" />
                  Acesso rápido
                </h3>
                <a
                  href="https://wa.me/5541987718296"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-h-[180px] flex-col justify-between rounded-3xl border border-black/10 bg-white/75 p-6 text-gray-950 shadow-xl shadow-black/5 transition hover:border-black/15 hover:bg-white"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/10 bg-black/[0.04]">
                    <FiHeadphones className="h-5 w-5" />
                  </span>
                  <span>
                    <strong className="block text-lg">Suporte WhatsApp</strong>
                    <span className="mt-1 block text-sm leading-relaxed text-gray-500">Fale diretamente com nossa equipe.</span>
                  </span>
                </a>
              </div>
            </div>

            {/* Seu Plano */}
            <SeuPlanoSection planos={planos} user={user} />
          </motion.div>
        </main>
        </div>
      </div>
    </div>
  );
};

export default PortalDashboard;
